import React from 'react';
import { MapPinIcon, ClockIcon, GlobeIcon, LanguagesIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { sections } from '../data/sections';
const section = sections[0];
const facts = [{
  icon: MapPinIcon,
  label: 'Based',
  value: 'London, United Kingdom'
}, {
  icon: GlobeIcon,
  label: 'Reach',
  value: 'UK, Europe, India and China'
}, {
  icon: ClockIcon,
  label: 'Availability',
  value: 'Selective further appointments'
}, {
  icon: LanguagesIcon,
  label: 'Working style',
  value: 'Direct, evidence-led, unhurried in judgement'
}];
const principles = [{
  title: 'Stewardship before spectacle',
  body: 'A board exists to protect an enterprise beyond the tenure of the people sitting around the table. Decisions are tested against the ten-year view, not the quarterly one.'
}, {
  title: 'Challenge with support',
  body: 'An executive team should leave a board meeting sharper and better backed, not merely examined. Scrutiny and confidence are not opposing forces.'
}, {
  title: 'Evidence over assertion',
  body: 'Papers should show workings. Where assurance is thin, that is the item worth an hour of the board’s time.'
}, {
  title: 'Everyone in the room counts',
  body: 'Employees, communities and the public purse are stakeholders in industrial businesses. Good governance keeps their interests visible at the table.'
}];
export function About() {
  return <div data-id="element-104" data-mp="28hkq8j1q8hcg">
      <PageHeader section={section} intro="Who Janice Munday is, how she works, and the principles that shape her contribution in a boardroom." data-id="element-105" data-mp="28ht34axunmnn" />

      <section className="mt-12 grid gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16" data-id="element-106" data-mp="2mb2901ml9q1f">
        <div className="space-y-5 text-[15px] leading-relaxed text-slateink" data-id="element-107" data-mp="2i85yky77d08k">
          <p className="text-lg leading-relaxed text-navy" data-id="element-108" data-mp="2w7x8amqorugr">
            Janice Munday has spent her working life at the point where
            industrial capability, public policy and corporate governance meet
            — first inside government, and now around the boardroom table.
          </p>
          <p data-id="element-109" data-mp="1sx1bonpg12hm">
            She began in the machinery of the United Kingdom state, rising to
            director level in the department responsible for business and
            trade. There she learned an unusual discipline: how to hold a
            long-term national interest steady while commercial, political and
            workforce pressures pull in different directions. That habit of
            patient, evidence-led judgement is what she carries into the
            non-executive roles she holds today.
          </p>
          <p data-id="element-110" data-mp="2gjptl9egcuy8">
            Colleagues describe her as calm in rooms that are not. She is
            frequently invited into organisations at consequential moments —
            an ownership change, a chief executive succession, a governance
            framework that has outgrown its business — because she is willing
            to slow a decision down until it is genuinely sound, and equally
            willing to make it once it is.
          </p>
          <p data-id="element-111" data-mp="evlonulm4m0a">
            Outside the boardroom her commitment is to the people who will
            inherit British industry: she is a persistent advocate for
            technical education, apprenticeships and routes into engineering
            for young people who would not otherwise find them.
          </p>
        </div>

        <aside data-id="element-112" data-mp="12lg0c8u744iy">
          <h2 className="text-[11px] font-semibold uppercase tracking-overline text-slateink-light" data-id="element-113" data-mp="12k4tu19d8io5">
            At a glance
          </h2>
          <dl className="mt-5 divide-y divide-slate-200 border border-slate-200 bg-white" data-id="element-114" data-mp="sni9reov5vn6">
            {facts.map(fact => {
            const Icon = fact.icon;
            return <div key={fact.label} className="flex gap-4 px-6 py-5" data-id="element-115" data-mp="3des90oi63k00">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" data-id="element-116" data-mp="3dbxqx3mmmb9r" />
                  <div data-id="element-117" data-mp="1137azuksvwnn">
                    <dt className="text-[10px] font-semibold uppercase tracking-overline text-slateink-light" data-id="element-118" data-mp="112o55c8fcypz">
                      {fact.label}
                    </dt>
                    <dd className="mt-1 text-[14px] leading-snug text-navy" data-id="element-119" data-mp="r6ulza07a6z8">
                      {fact.value}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </aside>
      </section>

      <section className="mt-16" aria-labelledby="philosophy" data-id="element-120" data-mp="1otjqprmaexos">
        <h2 id="philosophy" className="font-serif text-2xl leading-tight text-navy" data-id="element-121" data-mp="1ot1pshoojvaj">
          Governance philosophy
        </h2>
        <ul className="mt-8 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2" data-id="element-122" data-mp="1euuhen8gilk5">
          {principles.map(principle => <li key={principle.title} className="flex h-full flex-col bg-white p-7" data-id="element-123" data-mp="3vigbtm4ijsff">
              <h3 className="font-serif text-lg leading-snug text-navy" data-id="element-124" data-mp="3h06ekohy4ba">
                {principle.title}
              </h3>
              <p className="mt-auto pt-3 text-[14px] leading-relaxed text-slateink" data-id="element-125" data-mp="3s46n51zz59fe">
                {principle.body}
              </p>
            </li>)}
        </ul>
      </section>
    </div>;
}